import { useEffect, useState } from "react";
import { api, formatDate, formatApiError } from "@/lib/api";
import { useLiveRefresh } from "@/lib/live";
import { PageHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth, hasRole } from "@/context/AuthContext";

import { ROLE_NAMES as ROLES } from "@/config/roleViews";

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);

  const load = () => api.get("/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);
  useLiveRefresh("users", load);

  const delUser = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const isAdmin = hasRole(me, "admin");
  const financeUsers = users.filter((u) => u.role === "admin" || u.role === "accounts");

  return (
    <div>
      <PageHeader
        subtitle="Access Management"
        title="Users & Roles"
        actions={
          <div className="flex gap-2">
            {isAdmin && (
              <Dialog open={pinOpen} onOpenChange={setPinOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="manage-pins-btn" variant="outline" className="rounded-sm">
                    <KeyRound size={14} className="mr-1.5" />Manage Finance PINs
                  </Button>
                </DialogTrigger>
                <FinancePinDialog users={financeUsers} onDone={() => setPinOpen(false)} />
              </Dialog>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-user-btn" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]"><Plus size={16} className="mr-1.5" />Add User</Button>
              </DialogTrigger>
              <UserForm onDone={() => { setOpen(false); load(); }} />
            </Dialog>
          </div>
        }
      />
      <div className="p-8">
        <div className="border border-neutral-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-neutral-50 label-caps">
              <tr>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-left px-4 py-2.5">Email</th>
                <th className="text-left px-4 py-2.5">Role</th>
                <th className="text-left px-4 py-2.5">Created</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-neutral-100 data-row">
                  <td className="px-4 py-2.5 font-medium">{u.name}</td>
                  <td className="px-4 py-2.5 text-neutral-600">{u.email}</td>
                  <td className="px-4 py-2.5">
                    <span className="text-xs uppercase tracking-widest px-2 py-0.5 bg-neutral-100 rounded-sm">{u.role.replace("_", " ")}</span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-neutral-500">{formatDate(u.created_at)}</td>
                  <td className="px-4 py-2.5 text-right">
                    {u.id !== me?.id && (
                      <Button data-testid={`del-user-${u.email}`} size="sm" variant="ghost" onClick={() => delUser(u.id)} className="text-rose-700 hover:bg-rose-50 rounded-sm">
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function UserForm({ onDone }) {
  const [f, setF] = useState({ email: "", password: "", name: "", role: "operator" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/users", f); toast.success("User created"); onDone(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <DialogContent className="rounded-sm">
      <DialogHeader><DialogTitle className="font-display">New User</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div><label className="label-caps block mb-1.5">Name</label>
          <Input data-testid="user-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-sm" required /></div>
        <div><label className="label-caps block mb-1.5">Email</label>
          <Input data-testid="user-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="rounded-sm" required /></div>
        <div><label className="label-caps block mb-1.5">Password</label>
          <Input data-testid="user-password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="rounded-sm" required minLength={6} /></div>
        <div><label className="label-caps block mb-1.5">Role</label>
          <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
            <SelectTrigger data-testid="user-role" className="rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}</SelectContent>
          </Select></div>
        <DialogFooter><Button data-testid="user-submit" type="submit" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">Create</Button></DialogFooter>
      </form>
    </DialogContent>
  );
}

function FinancePinDialog({ users, onDone }) {
  const [userId, setUserId] = useState("");
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pin !== confirm) { toast.error("PINs do not match"); return; }
    if (pin.length < 4) { toast.error("PIN must be at least 4 digits"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/finance-pin/admin-set", {
        user_id: userId, new_pin: pin,
      });
      toast.success(`Finance PIN set for ${data.name || data.email}`);
      setUserId(""); setPin(""); setConfirm("");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const reset = async (uid) => {
    if (!window.confirm("Clear this user's Finance PIN? They'll be forced to set a new one next time they open Sales / Purchases.")) return;
    try {
      await api.post(`/auth/finance-pin/reset?user_id=${encodeURIComponent(uid)}`);
      toast.success("PIN cleared — user will set a new one on next unlock");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DialogContent className="rounded-sm max-w-lg">
      <DialogHeader>
        <DialogTitle className="font-display flex items-center gap-2">
          <KeyRound size={18} className="text-[#FF5A00]" />
          Manage Finance PINs
        </DialogTitle>
      </DialogHeader>
      <div className="text-xs text-neutral-500 mb-3 leading-relaxed">
        Only <b>Admin</b> and <b>Accounts</b> roles use Finance PINs (to open Sales, Purchases, and revenue reports).
        Setting a PIN here overrides any existing one. Use <b>Reset</b> to force the user to pick a new PIN next time.
      </div>

      <form onSubmit={submit} className="space-y-3 border border-neutral-200 p-4 bg-neutral-50/40">
        <div>
          <label className="label-caps block mb-1.5">Select user</label>
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger data-testid="pin-user-select" className="rounded-sm bg-white"><SelectValue placeholder="Pick admin or accounts user…" /></SelectTrigger>
            <SelectContent>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.name} · {u.email} · {u.role}
                </SelectItem>
              ))}
              {users.length === 0 && <SelectItem value="none" disabled>No finance-eligible users yet</SelectItem>}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-caps block mb-1.5">New PIN (4-12 digits)</label>
            <Input
              data-testid="pin-new"
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              maxLength={12}
              className="rounded-sm text-lg font-mono-num tracking-widest text-center"
              placeholder="••••"
            />
          </div>
          <div>
            <label className="label-caps block mb-1.5">Confirm PIN</label>
            <Input
              data-testid="pin-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))}
              maxLength={12}
              className="rounded-sm text-lg font-mono-num tracking-widest text-center"
              placeholder="••••"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            data-testid="pin-set-submit"
            type="submit"
            disabled={busy || !userId || pin.length < 4}
            className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]"
          >
            {busy ? "Saving…" : "Set PIN"}
          </Button>
        </div>
      </form>

      {users.length > 0 && (
        <div className="mt-4 border border-neutral-200">
          <div className="px-3 py-2 border-b border-neutral-200 label-caps bg-neutral-50">Quick Reset</div>
          <div className="divide-y divide-neutral-100">
            {users.map((u) => (
              <div key={u.id} className="px-3 py-2 flex items-center justify-between">
                <div className="text-sm">
                  <span className="font-medium">{u.name}</span>
                  <span className="text-neutral-500 text-xs ml-1.5">· {u.email} · {u.role}</span>
                </div>
                <button
                  type="button"
                  data-testid={`pin-reset-${u.email}`}
                  onClick={() => reset(u.id)}
                  className="text-xs uppercase tracking-widest text-rose-700 hover:underline"
                >
                  Reset PIN
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </DialogContent>
  );
}
